import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { APP_PATH } from "../api/backendPath";
import { getAccessToken, getTokenUserId } from "../utils/socketAuth";
import {
  ADMIN_CHAT_READ_EVENT,
  ADMIN_PROVIDER_CONTEXT,
  CHAT_SOCKET_CONNECTED_EVENT,
  isAdminSupportMessage,
} from "../utils/chatEvents";

// Last known count per user. Every page mounts its own layout, so without this the badge would
// flash to 0 on each navigation until the server answers.
const lastKnown = { userId: null, count: 0 };

/**
 * Unread count for the sidebar "Chat" link — the provider's support chat with the admin team.
 *
 * Counts ONLY messages of the support room (room_context "admin_provider") written by someone
 * else. Patient conversations have their own badges (chat icons, ChatDock) and never touch it.
 * The count is loaded from the server (unread messages of the support room) on mount, after every
 * socket reconnect and after the chat page marks the room read, so it survives navigation and a
 * reload instead of living only in component state.
 *
 * @param socket      the shared notification socket (useSocketNotifications)
 * @param onChatPage  true while the support chat page is shown (it reads the messages itself)
 * @param onAdminMessage optional callback for a new admin message (sound / toast)
 */
export default function useAdminChatUnread({
  socket,
  onChatPage = false,
  onAdminMessage,
} = {}) {
  const [count, setCount] = useState(() =>
    String(lastKnown.userId) === String(getTokenUserId())
      ? lastKnown.count
      : 0,
  );
  const onChatPageRef = useRef(onChatPage);
  onChatPageRef.current = onChatPage;
  const onAdminMessageRef = useRef(onAdminMessage);
  onAdminMessageRef.current = onAdminMessage;
  const reconcileTimerRef = useRef(null);

  const commit = useCallback((next) => {
    lastKnown.userId = getTokenUserId();
    lastKnown.count = next;
    setCount(next);
  }, []);

  const refresh = useCallback(async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}${APP_PATH.consultationChatRooms}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const rooms = res?.data?.data?.rooms || res?.data?.rooms || [];
      const unread = rooms
        .filter((r) => r.room_context === ADMIN_PROVIDER_CONTEXT)
        .reduce((sum, r) => sum + Number(r.unread_count || 0), 0);
      commit(onChatPageRef.current ? 0 : unread);
    } catch (e) {
      // Non-critical: keep the current count.
    }
  }, [commit]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (onChatPage) commit(0);
  }, [onChatPage, commit]);

  useEffect(() => {
    const onSync = () => refresh();
    window.addEventListener(CHAT_SOCKET_CONNECTED_EVENT, onSync);
    window.addEventListener(ADMIN_CHAT_READ_EVENT, onSync);
    return () => {
      window.removeEventListener(CHAT_SOCKET_CONNECTED_EVENT, onSync);
      window.removeEventListener(ADMIN_CHAT_READ_EVENT, onSync);
    };
  }, [refresh]);

  useEffect(() => {
    if (!socket) return undefined;
    const onNewMessage = (data) => {
      if (!isAdminSupportMessage(data)) return;
      if (Number(data?.sender_id) === Number(getTokenUserId())) return;
      // The chat page shows the message and marks it read.
      if (onChatPageRef.current) return;
      setCount((prev) => {
        const next = prev + 1;
        lastKnown.userId = getTokenUserId();
        lastKnown.count = next;
        return next;
      });
      onAdminMessageRef.current?.(data);
      // Reconcile with the server shortly after (a refresh already in flight may have returned
      // a count from before this message).
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
      reconcileTimerRef.current = setTimeout(() => {
        reconcileTimerRef.current = null;
        refresh();
      }, 1500);
    };
    socket.on("new_message", onNewMessage);
    return () => socket.off("new_message", onNewMessage);
  }, [socket, refresh]);

  useEffect(
    () => () => {
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
    },
    [],
  );

  return { count, refresh };
}
